/**
 * Hook: useFeatureFlag
 *
 * Fornece acesso a feature flags com suporte a:
 * - Rollout percentual (gradual)
 * - Beta testers (lista explícita)
 * - Development mode (força ativação)
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/features/auth';
import {
  FEATURE_FLAGS,
  FeatureFlagName,
  getFeatureFlagConfig
} from '@/config/features/flags';
import { featureFlagsService } from '@/services/featureFlags/featureFlagsService';

/**
 * Hook principal para usar feature flags
 *
 * @param flagName - Nome da flag (type-safe)
 * @returns boolean - Se a flag está ativada para o usuário
 *
 * @example
 * ```tsx
 * const TimelineEnabled = useFeatureFlag('funil.timeline.view');
 *
 * if (!TimelineEnabled) return null;
 * return <Timeline />;
 * ```
 */
export function useFeatureFlag(flagName: FeatureFlagName): boolean {
  const { user, profile, state } = useAuth();
  const [isEnabled, setIsEnabled] = useState(() => {
    const config = getFeatureFlagConfig(flagName);
    return config.enabled && config.rolloutPercentage > 0;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const checkFlag = async () => {
      try {
        // Se auth ainda está carregando, usar valor default
        if (state.isLoading) {
          console.log(`[useFeatureFlag] Auth still loading for ${flagName}, keeping default`);
          setLoading(false);
          return;
        }

        // Se usuário não autenticado, usar valor default
        if (!user || !profile) {
          const config = getFeatureFlagConfig(flagName);
          const defaultValue = config.enabled && config.rolloutPercentage > 0;
          console.log(`[useFeatureFlag] No user/profile for ${flagName}, using default:`, defaultValue);
          setIsEnabled(defaultValue);
          setLoading(false);
          return;
        }

        console.log(`[useFeatureFlag] Checking ${flagName} for user ${user.id} (${user.email})`);

        // Buscar valor real da flag do servidor
        const value = await featureFlagsService.checkFeatureFlag(
          flagName,
          user.id,
          user.email
        );

        console.log(`[useFeatureFlag] ${flagName} result for ${user.email}:`, value);
        setIsEnabled(value);
      } catch (err) {
        console.error(`Erro ao checar feature flag ${flagName}:`, err);
        // Em caso de erro, usa valor default (conservador)
        const config = getFeatureFlagConfig(flagName);
        const fallbackValue = config.enabled && config.rolloutPercentage > 0;
        console.log(`[useFeatureFlag] Error for ${flagName}, using fallback:`, fallbackValue);
        setIsEnabled(fallbackValue);
        setError(err instanceof Error ? err : new Error('Unknown error'));
      } finally {
        setLoading(false);
      }
    };

    checkFlag();
  }, [flagName, user?.id, user?.email, state.isLoading]);

  return isEnabled;
}

/**
 * Hook para ambiente de desenvolvimento
 * Força a ativação de flags quando ?debug=true
 *
 * @param flagName - Nome da flag
 * @returns boolean - Se a flag está ativada (forçada em debug mode)
 *
 * @example
 * ```tsx
 * const TimelineEnabled = useFeatureFlagDebug('funil.timeline.view');
 * // Ativa se URL tem ?debug=true
 * ```
 */
export function useFeatureFlagDebug(flagName: FeatureFlagName): boolean {
  const baseEnabled = useFeatureFlag(flagName);

  // Se URL tem debug=true, força ativação
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('debug') === 'true') {
      return true;
    }
  }

  return baseEnabled;
}

/**
 * Hook para obter múltiplas flags de uma vez
 *
 * @param flagNames - Array de nomes de flags
 * @returns Record com status de cada flag
 *
 * @example
 * ```tsx
 * const flags = useFeatureFlags(['funil.timeline.view', 'funil.pipeline.view']);
 * // { 'funil.timeline.view': true, 'funil.pipeline.view': false }
 * ```
 */
export function useFeatureFlags(
  flagNames: FeatureFlagName[]
): Record<FeatureFlagName, boolean> {
  const results: Record<FeatureFlagName, boolean> = {} as any;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  flagNames.forEach(flagName => {
    results[flagName] = useFeatureFlag(flagName);
  });

  return results;
}

/**
 * Hook para obter informações completas sobre uma flag
 * (não apenas se está ativada, mas também por quê)
 *
 * @param flagName - Nome da flag
 * @returns Objeto com detalhes da flag
 *
 * @example
 * ```tsx
 * const flagInfo = useFeatureFlagInfo('funil.timeline.view');
 * // {
 * //   enabled: true,
 * //   rolloutPercentage: 25,
 * //   isBetaTester: false,
 * //   isInRollout: true,
 * //   reason: 'In 25% rollout'
 * // }
 * ```
 */
export function useFeatureFlagInfo(flagName: FeatureFlagName) {
  const { user } = useAuth();
  const enabled = useFeatureFlag(flagName);
  const config = getFeatureFlagConfig(flagName);

  const isBetaTester = user?.email
    ? (config.betaTesters || []).includes(user.email)
    : false;

  const userHash = user?.id
    ? hashUserId(user.id)
    : 0;
  const threshold = (config.rolloutPercentage / 100) * 0xffffffff;
  const isInRollout = userHash < threshold;

  let reason = 'Desativado';
  if (!config.enabled) {
    reason = 'Flag desativada globalmente';
  } else if (isBetaTester) {
    reason = 'Beta tester explícito';
  } else if (isInRollout) {
    reason = `Rollout de ${config.rolloutPercentage}%`;
  }

  return {
    enabled,
    rolloutPercentage: config.rolloutPercentage,
    isBetaTester,
    isInRollout,
    reason,
    config
  };
}

/**
 * Simples hash de user ID para rollout percentual
 * Garante que mesmo usuário sempre cai na mesma faixa
 */
function hashUserId(userId: string): number {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & 0xffffffff; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Type para componentes que usam feature flags
 * Garante que componentes condicionales tem fallback
 */
export interface FeatureFlagComponentProps {
  flagName: FeatureFlagName;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showDebugInfo?: boolean;
}

/**
 * Componente wrapper para renderizar condicionalmente baseado em flag
 *
 * @example
 * ```tsx
 * <FeatureFlagGate flagName="funil.timeline.view">
 *   <Timeline />
 * </FeatureFlagGate>
 * ```
 */
export function FeatureFlagGate({
  flagName,
  children,
  fallback = null,
  showDebugInfo = false
}: FeatureFlagComponentProps) {
  const enabled = useFeatureFlag(flagName);
  const debugInfo = showDebugInfo ? useFeatureFlagInfo(flagName) : null;

  if (!enabled) {
    if (showDebugInfo) {
      return (
        <div style={{ padding: '16px', background: '#f5f5f5', borderRadius: '4px' }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
            Flag desativada: {flagName}
          </p>
          <p style={{ margin: '0', fontSize: '12px', color: '#666' }}>
            {debugInfo?.reason}
          </p>
        </div>
      );
    }
    return fallback;
  }

  return <>{children}</>;
}
